<script lang="ts">
  import { orgPlus, orgUser } from "@nrk/origo";
  import {
    ROLES,
    digitalSourceTypePhotography,
    getPhotographer,
    isPhotographer,
    type Contact,
    type Contributor,
    type Image,
  } from "bildebanken-model";
  import type { LpAuthority } from "services/authority";
  import {
    authorityToContact,
    currentUserAuthority,
    replaceOrigoSuggestRequest,
  } from "services/authority";
  import { onMount } from "svelte";
  import { updateField, type EditImageActor } from "./editImageMachine";
  import InfoBox from "./InfoBox.svelte";
  import { getName } from "../../auth/auth";

  export let service: EditImageActor;
  export let image: Image;

  $: photographerContact = getPhotographer(image)?.contact;
  let photographerName;

  onMount(() => {
    photographerName =
      image.metadata.contributors?.find((c) => isPhotographer(c))?.contact.title || "";
  });

  let suggestEl: HTMLElement;

  document.addEventListener("suggest-select", (event: any) => {
    const contact: Contact = {
      title: event.target.value.name,
      resId: event.target.value.uri,
    };
    setPhotographer(contact);
  });
  document.addEventListener("suggest.ajax.beforeSend", replaceOrigoSuggestRequest);
  onMount(() => {
    suggestEl?.addEventListener("suggest.select", onSelectPhotographer);
    return () => {
      suggestEl?.removeEventListener("suggest.select", onSelectPhotographer);
    };
  });

  function setPhotographer(contact: Contact) {
    const oldContributors = image.metadata.contributors ?? [];
    const newContributors: Contributor[] = oldContributors.filter((c) => !isPhotographer(c));

    newContributors.push({
      contact,
      role: { resId: ROLES.photographer },
    });

    photographerName = contact.title || "";
    updateField(service, "contributors", newContributors);
  }

  function onSelectPhotographer(event: CustomEvent & { target: { value: LpAuthority } }) {
    const value = event.target.value;
    if (value) {
      setPhotographer(authorityToContact(value));
    }
  }

  function onBlur(this: HTMLInputElement) {
    if (this.value !== photographerContact?.title) {
      setPhotographer({ title: this.value });
    }
  }

  function setCurrentUserAsPhotographer() {
    if (currentUserAuthority) {
      setPhotographer(authorityToContact(currentUserAuthority));
    }
  }
</script>

<label for="creator">
  <span class="user-icon">{@html orgUser}</span>
  {#if image.metadata.digitalSourceType === digitalSourceTypePhotography}
    Fotograf
  {:else}
    Opphavsperson
  {/if}
</label>
<InfoBox
  header={`Hva er fotograf/opphavsperson?`}
  link="https://portalen.nrk.no/fotografering-filming-og-publisering-av-bilder-og-video-juridisk/?/lchhz4emnvt"
>
  <ul class="content">
    <li>Fotograf er personen som har tatt bildet.</li>
    <li>
      Opphavsperson er personen som har skapt et åndsverk, for eksempel grafikk, illustrasjon eller
      et komponert bilde.
    </li>
  </ul>
</InfoBox>
<span>
  <input
    id="creator"
    class="org-input"
    type="text"
    value={photographerName || ""}
    placeholder=""
    autocomplete="off"
    on:blur={onBlur}
  />
  <bb-suggest query="size=5&source=lp&alive=true" type="agent" hidden bind:this={suggestEl} />
  {#if currentUserAuthority && photographerContact?.resId !== currentUserAuthority.uri}
    <button type="button" class="org-tag org-tag--suggestive" on:click={setCurrentUserAsPhotographer}>
      {@html orgPlus} <span style="margin-left: 0.25rem">Legg til "{getName()}"</span>
    </button>
  {/if}
</span>

<style>
  .user-icon {
    font-size: 0.6rem;
    position: relative;
    bottom: 2px;
  }

  .content {
    font-size: 12px;
    margin-top: 6px;
    margin-bottom: 6px;
  }
  button {
    margin: var(--org-xsmall);
  }
</style>
